import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, AlertTriangle, Calendar, RefreshCw, Activity, Radio } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

interface BikeDowntimeData {
  id: string;
  serialNumber: string;
  model: string;
  status: 'available' | 'in_use' | 'maintenance' | 'retired';
  lastRentalEndDate: string | null;
  daysInDowntime: number;
  gpsTag: string | null;
  priority: 'critical' | 'high' | 'medium' | 'low';
  neverRented: boolean;
}

interface DowntimeStats {
  critical: number;
  longDowntime: number;
  availableUnused: number;
  totalInDowntime: number;
}

export const BikeDowntimeAnalytics: React.FC = () => {
  const [bikes, setBikes] = useState<BikeDowntimeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [stats, setStats] = useState<DowntimeStats>({
    critical: 0,
    longDowntime: 0,
    availableUnused: 0,
    totalInDowntime: 0
  });

  const fetchDowntimeData = async () => {
    setLoading(true);
    try {
      console.log('Начинаем загрузку данных о простоях велосипедов...');
      
      // Получаем данные о велосипедах с информацией о последней аренде
      const { data: bikesData, error } = await supabase
        .from('bikes')
        .select(`
          id,
          serial_number,
          model,
          status,
          gps_tag,
          rentals!rentals_bike_id_fkey(
            end_date
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Ошибка при получении данных велосипедов:', error);
        throw error;
      }

      console.log('Получены данные велосипедов:', bikesData?.length || 0);

      const processedBikes: BikeDowntimeData[] = [];
      const now = new Date();

      for (const bike of bikesData || []) {
        // Находим последнюю завершенную аренду
        const completedRentals = bike.rentals?.filter(r => r.end_date) || [];
        const lastRental = completedRentals.sort((a, b) => 
          new Date(b.end_date).getTime() - new Date(a.end_date).getTime()
        )[0];

        const lastRentalEndDate = lastRental?.end_date || null;
        const neverRented = !lastRentalEndDate;
        
        // Рассчитываем дни простоя
        let daysInDowntime = 0;
        if (lastRentalEndDate) {
          const endDate = new Date(lastRentalEndDate);
          daysInDowntime = Math.floor((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
        } else {
          // Если аренд не было, считаем как очень долгий простой для статистики
          daysInDowntime = 999;
        }

        // Определяем приоритет
        let priority: BikeDowntimeData['priority'];
        if (neverRented || daysInDowntime > 30) priority = 'critical';
        else if (daysInDowntime > 15) priority = 'high';
        else if (daysInDowntime > 7) priority = 'medium';
        else priority = 'low';

        processedBikes.push({
          id: bike.id,
          serialNumber: bike.serial_number || 'Не указан',
          model: bike.model || 'Не указана',
          status: bike.status as 'available' | 'in_use' | 'maintenance' | 'retired',
          lastRentalEndDate,
          daysInDowntime,
          gpsTag: bike.gps_tag,
          priority,
          neverRented
        });
      }

      // Сортируем: сначала никогда не арендованные, потом по количеству дней простоя (убывание)
      processedBikes.sort((a, b) => {
        if (a.neverRented && !b.neverRented) return -1;
        if (!a.neverRented && b.neverRented) return 1;
        if (a.neverRented && b.neverRented) return 0;
        return b.daysInDowntime - a.daysInDowntime;
      });

      // Рассчитываем статистику
      const newStats = {
        critical: processedBikes.filter(b => b.priority === 'critical').length,
        longDowntime: processedBikes.filter(b => !b.neverRented && b.daysInDowntime > 15).length,
        availableUnused: processedBikes.filter(b => b.status === 'available' && (b.neverRented || b.daysInDowntime > 7)).length,
        totalInDowntime: processedBikes.filter(b => b.neverRented || b.daysInDowntime > 0).length
      };

      console.log('Обработанная статистика:', newStats);
      console.log('Обработано велосипедов:', processedBikes.length);

      setBikes(processedBikes);
      setStats(newStats);
    } catch (error) {
      console.error('Ошибка при загрузке данных о простоях:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDowntimeData();
  }, []);

  const filteredBikes = bikes.filter(bike => {
    const statusMatch = statusFilter === 'all' || bike.status === statusFilter;
    const priorityMatch = priorityFilter === 'all' || bike.priority === priorityFilter;
    return statusMatch && priorityMatch;
  });

  const getPriorityBadge = (priority: BikeDowntimeData['priority'], days: number, neverRented: boolean) => {
    const configs = {
      critical: { label: 'Критический', className: 'bg-red-100 text-red-800' },
      high: { label: 'Высокий', className: 'bg-orange-100 text-orange-800' },
      medium: { label: 'Средний', className: 'bg-yellow-100 text-yellow-800' },
      low: { label: 'Низкий', className: 'bg-green-100 text-green-800' }
    };

    const config = configs[priority];
    const displayText = neverRented ? 'Не арендовался' : `${days}д`;
    
    return (
      <Badge variant="outline" className={config.className}>
        {config.label} ({displayText})
      </Badge>
    );
  };

  const getStatusBadge = (status: BikeDowntimeData['status']) => {
    const configs = {
      available: { label: 'Доступен', className: 'bg-green-100 text-green-800' },
      in_use: { label: 'В работе', className: 'bg-blue-100 text-blue-800' },
      maintenance: { label: 'Обслуживание', className: 'bg-gray-100 text-gray-800' },
      retired: { label: 'Списан', className: 'bg-gray-100 text-gray-600' }
    };

    const config = configs[status];
    return (
      <Badge variant="outline" className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatLastRental = (date: string | null, neverRented: boolean) => {
    if (neverRented) return 'Никогда не арендовался';
    
    try {
      return formatDistanceToNow(new Date(date!), { 
        addSuffix: true, 
        locale: ru 
      });
    } catch {
      return 'Ошибка даты';
    }
  };

  const formatDowntimeDays = (days: number, neverRented: boolean) => {
    if (neverRented) {
      return <span className="text-red-600 font-medium">Не арендовался</span>;
    }
    
    return (
      <span className={`font-medium ${
        days > 30 ? 'text-red-600' :
        days > 15 ? 'text-orange-600' :
        days > 7 ? 'text-yellow-600' : 'text-green-600'
      }`}>
        {days} дней
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Статистические карточки */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Критический простой</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
            )}
            <p className="text-xs text-muted-foreground">Более 30 дней или не арендовались</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Долгий простой</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <div className="text-2xl font-bold text-orange-600">{stats.longDowntime}</div>
            )}
            <p className="text-xs text-muted-foreground">Более 15 дней</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Доступные неиспользуемые</CardTitle>
            <Activity className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <div className="text-2xl font-bold text-yellow-600">{stats.availableUnused}</div>
            )}
            <p className="text-xs text-muted-foreground">Доступен, но не используется</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Всего в простое</CardTitle>
            <Calendar className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <div className="text-2xl font-bold text-blue-600">{stats.totalInDowntime}</div>
            )}
            <p className="text-xs text-muted-foreground">Всего велосипедов</p>
          </CardContent>
        </Card>
      </div>

      {/* Фильтры и действия */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Анализ простоев велосипедов
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="available">Доступен</SelectItem>
                  <SelectItem value="in_use">В работе</SelectItem>
                  <SelectItem value="maintenance">Обслуживание</SelectItem>
                  <SelectItem value="retired">Списан</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Приоритет" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все приоритеты</SelectItem>
                  <SelectItem value="critical">Критический</SelectItem>
                  <SelectItem value="high">Высокий</SelectItem>
                  <SelectItem value="medium">Средний</SelectItem>
                  <SelectItem value="low">Низкий</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchDowntimeData}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Обновить
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Модель</TableHead>
                  <TableHead>Серийный номер</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Последняя аренда</TableHead>
                  <TableHead>Дней в простое</TableHead>
                  <TableHead>IoT</TableHead>
                  <TableHead>Приоритет</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBikes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Нет данных для отображения
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBikes.map((bike) => (
                    <TableRow key={bike.id}>
                      <TableCell className="font-medium">{bike.model}</TableCell>
                      <TableCell className="font-mono text-sm">{bike.serialNumber}</TableCell>
                      <TableCell>{getStatusBadge(bike.status)}</TableCell>
                      <TableCell>{formatLastRental(bike.lastRentalEndDate, bike.neverRented)}</TableCell>
                      <TableCell>
                        {formatDowntimeDays(bike.daysInDowntime, bike.neverRented)}
                      </TableCell>
                      <TableCell>
                        {bike.gpsTag ? (
                          <div className="flex items-center gap-1">
                            <Radio className="h-4 w-4" />
                            <span className="font-mono text-sm">{bike.gpsTag}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Не указан</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {getPriorityBadge(bike.priority, bike.daysInDowntime, bike.neverRented)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Информационная панель */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Критерии оценки простоя</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <span className="font-medium text-red-600">Критический:</span> более 30 дней без аренды или никогда не арендовались
            </div>
            <div>
              <span className="font-medium text-orange-600">Высокий:</span> 15-30 дней без аренды
            </div>
            <div>
              <span className="font-medium text-yellow-600">Средний:</span> 7-15 дней без аренды
            </div>
            <div>
              <span className="font-medium text-green-600">Низкий:</span> менее 7 дней без аренды
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
